import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authAPI } from '../services/api'
import toast from 'react-hot-toast'
import i18n from '../i18n'

// TODO: Security — migrate to HttpOnly cookies to protect tokens from XSS
// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: false,
  isLoading: true,
  error: null
}

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR'
}

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken
      }
    
    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      }
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      }
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      }
    
    default:
      return state
  }
}

// Context
const AuthContext = createContext()

// Provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START })
      
      const response = await authAPI.login(credentials)
      
      if (response.success) {
        const { user, token, refreshToken } = response.data
        
        // Store tokens in localStorage
        localStorage.setItem('token', token)
        localStorage.setItem('refreshToken', refreshToken)
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token, refreshToken }
        })
        
        return { success: true }
      } else {
        throw new Error(response.message || 'Błąd logowania')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd logowania'
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      })
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START })
      
      const response = await authAPI.register(userData)
      
      if (response.success) {
        const { user, token, refreshToken } = response.data
        
        // Store tokens in localStorage
        localStorage.setItem('token', token)
        localStorage.setItem('refreshToken', refreshToken)
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token, refreshToken }
        })
        
        return { success: true }
      } else {
        throw new Error(response.message || 'Błąd rejestracji')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd rejestracji'
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      })
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Logout function
  const logout = async () => {
    try {
      // Call logout API
      await authAPI.logout()
    } catch (error) {
      console.error('Logout API error:', error)
    } finally {
      // Clear localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT })
      toast.success(i18n.t('auth:toast.logoutSuccess'))
    }
  }

  // Refresh token function
  const refreshAuthToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('Brak refresh token')
      }

      const response = await authAPI.refreshToken({ refreshToken })
      
      if (response.success) {
        const { token, refreshToken: newRefreshToken } = response.data
        
        localStorage.setItem('token', token)
        localStorage.setItem('refreshToken', newRefreshToken)
        
        dispatch({
          type: AUTH_ACTIONS.REFRESH_TOKEN,
          payload: { token, refreshToken: newRefreshToken }
        })
        
        return token
      } else {
        throw new Error('Błąd odświeżania tokenu')
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      return null
    }
  }

  // Update profile function
  const updateProfile = async (profileData, { silent = false } = {}) => {
    try {
      const response = await authAPI.updateProfile(profileData)

      if (response.success) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_PROFILE,
          payload: response.data.user
        })

        if (!silent) toast.success(i18n.t('auth:toast.profileUpdated'))
        return { success: true }
      } else {
        throw new Error(response.message || 'Błąd aktualizacji profilu')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message
      if (!silent) toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData)
      
      if (response.success) {
        toast.success(i18n.t('auth:toast.passwordChanged'))
        return { success: true }
      } else {
        throw new Error(response.message || 'Błąd zmiany hasła')
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token')
      
      if (token) {
        try {
          const response = await authAPI.getProfile()
          
          if (response.success) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: response.data.user,
                token,
                refreshToken: localStorage.getItem('refreshToken')
              }
            })
          } else {
            // Token invalid, try to refresh
            await refreshAuthToken()
          }
        } catch (error) {
          console.error('Auth check failed:', error)
          // Clear invalid tokens
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          dispatch({ type: AUTH_ACTIONS.LOGOUT })
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }
    }

    checkAuth()
  }, [])

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  const value = {
    ...state,
    login,
    register,
    logout,
    refreshAuthToken,
    updateProfile,
    changePassword,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
